import { useState, useEffect, useRef } from 'react';
import { Lock, Loader2, ShieldCheck } from 'lucide-react';
import StandardModal from './StandardModal';
import { SecondaryButton, PrimaryButton } from './Buttons';
import { Input } from './ui/input';

/**
 * PIN confirmation for sensitive Platform Admin actions.
 * Usage: parent keeps `pinPrompt` state ({ label, run }) and renders
 * <PinModal prompt={pinPrompt} onClose={() => setPinPrompt(null)} />.
 * `run(pin)` should throw on failure (axios error) — 403 PIN_INVALID and
 * 428 PIN_NOT_SET/PIN_REQUIRED get friendly messages.
 */
const PinModal = ({ prompt, onClose }) => {
  const [pin, setPin] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (prompt) {
      setPin('');
      setError('');
      setBusy(false);
      setTimeout(() => inputRef.current?.focus(), 150);
    }
  }, [prompt]);

  const submit = async () => {
    if (!/^\d{4}$/.test(pin)) {
      setError('Enter your 4-digit PIN');
      return;
    }
    setBusy(true);
    setError('');
    try {
      await prompt.run(pin);
      onClose();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail === 'PIN_INVALID') setError('Wrong PIN — this attempt has been logged.');
      else if (detail === 'PIN_NOT_SET') setError("You don't have a PIN yet. The owner can set one in Team → Edit → Access.");
      else if (typeof detail === 'string') setError(detail);
      else setError(err?.response?.status ? `Request failed (HTTP ${err.response.status}) — please try again` : 'Network error — please try again');
    } finally {
      setBusy(false);
    }
  };

  return (
    <StandardModal
      isOpen={!!prompt}
      onClose={busy ? () => {} : onClose}
      title="Confirm with your PIN"
      size="sm"
      footer={
        <>
          <SecondaryButton onClick={onClose} className="flex-1" disabled={busy}>Cancel</SecondaryButton>
          <PrimaryButton onClick={submit} className="flex-1" disabled={busy} data-testid="pin-confirm-button">
            {busy ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" />Confirming...</>) : (<><ShieldCheck className="w-4 h-4 mr-2" />Confirm</>)}
          </PrimaryButton>
        </>
      }
    >
      <div className="space-y-4">
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
            <Lock className="w-4 h-4 text-blue-600" />
          </div>
          <p className="text-sm text-gray-600 pt-1.5" data-testid="pin-modal-action-label">{prompt?.label}</p>
        </div>
        <Input
          ref={inputRef}
          type="password"
          inputMode="numeric"
          maxLength={4}
          value={pin}
          onChange={(e) => setPin(e.target.value.replace(/\D/g, ''))}
          onKeyDown={(e) => e.key === 'Enter' && submit()}
          placeholder="••••"
          className="h-12 text-center text-2xl tracking-[0.5em] font-mono"
          data-testid="pin-input"
        />
        {error && <p className="text-sm text-red-600" data-testid="pin-error">{error}</p>}
        <p className="text-xs text-gray-400">This action is recorded in the audit trail.</p>
      </div>
    </StandardModal>
  );
};

export default PinModal;
